import {
  Background,
  Controls,
  type DefaultEdgeOptions,
  type Edge,
  type EdgeMouseHandler,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useGraphElements } from "@/hooks/use-graph-elements";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";
import { CanvasControls } from "./CanvasControls";
import { DataViewSwitch } from "./DataViewSwitch";
import { ErdEdgeMarkers } from "./ErdEdgeMarkers";
import { ActorNode } from "./nodes/ActorNode";
import { ClusterFrameNode } from "./nodes/ClusterFrame";
import { ComponentNode } from "./nodes/ComponentNode";
import { ContainerNode } from "./nodes/ContainerNode";
import { EndpointNode } from "./nodes/EndpointNode";
import { EntityNode } from "./nodes/EntityNode";
import { ExternalSystemNode } from "./nodes/ExternalSystemNode";
import { PermissionNode } from "./nodes/PermissionNode";
import { SystemBoundaryNode } from "./nodes/SystemBoundaryNode";
import { SystemFrameNode } from "./nodes/SystemFrameNode";
import { SystemNode } from "./nodes/SystemNode";

const NODE_TYPES = {
  actor: ActorNode,
  externalSystem: ExternalSystemNode,
  container: ContainerNode,
  component: ComponentNode,
  // L3 data-model view — entities of a data-storage container.
  entity: EntityNode,
  // v0.4 — L3-of-external API surface entities.
  permission: PermissionNode,
  endpoint: EndpointNode,
  // v0.2 docs: abstract boundary, not a selectable entity.
  systemBoundary: SystemBoundaryNode,
  // v0.3 docs: the authored System entity — selectable, opens DetailPanel.
  system: SystemNode,
  systemFrame: SystemFrameNode,
  // L2 sub-boundary chrome (BACKGROUND WORKERS, EXTERNAL SERVICES).
  clusterFrame: ClusterFrameNode,
};

function stripPrefix(id: string): string {
  const idx = id.indexOf(":");
  return idx === -1 ? id : id.slice(idx + 1);
}

// systemFrame is the decorative outer chrome around L2/L3 — never participates
// in the focus choreography. systemBoundary (v0.2's abstract central box) IS a
// relationship endpoint at L1: every actor → system → external edge passes
// through it, so it must dim and light up like any other node. It's only
// blocked from being the *origin* of a focus (handled in onNodeClick).
// clusterFrame is L2 sub-boundary chrome (WORKERS, EXTERNAL SERVICES) — same
// non-focusable rule as systemFrame.
const NON_FOCUSABLE_TYPES = new Set(["systemFrame", "clusterFrame"]);

// Default to direct/diagonal lines (point-to-point, no orthogonal bends)
// across L1/L2/L3 uniformly. Per-edge overrides (e.g. dashed actor edges'
// `style`) merge over this.
const DEFAULT_EDGE_OPTIONS: DefaultEdgeOptions = {
  type: "straight",
};

// Display label for a node, used by the search filter. Each node type
// stashes its label in a different shape — actor.label, container.label,
// system.label, or the bare data.label for v0.2 SystemBoundary / frames.
function nodeLabelFor(node: Node): string {
  const data = node.data as Record<string, unknown> | undefined;
  if (!data) return "";
  if (typeof data.label === "string") return data.label;
  for (const key of ["system", "container", "actor"] as const) {
    const inner = data[key];
    if (inner && typeof inner === "object" && "label" in inner) {
      const l = (inner as { label: unknown }).label;
      if (typeof l === "string") return l;
    }
  }
  return "";
}

function buildAdjacency(edges: ReadonlyArray<Edge>) {
  const nodeToEdges = new Map<string, Set<string>>();
  const nodeToPeers = new Map<string, Set<string>>();
  for (const e of edges) {
    let srcEdges = nodeToEdges.get(e.source);
    if (!srcEdges) {
      srcEdges = new Set<string>();
      nodeToEdges.set(e.source, srcEdges);
    }
    srcEdges.add(e.id);
    let tgtEdges = nodeToEdges.get(e.target);
    if (!tgtEdges) {
      tgtEdges = new Set<string>();
      nodeToEdges.set(e.target, tgtEdges);
    }
    tgtEdges.add(e.id);
    let srcPeers = nodeToPeers.get(e.source);
    if (!srcPeers) {
      srcPeers = new Set<string>();
      nodeToPeers.set(e.source, srcPeers);
    }
    srcPeers.add(e.target);
    let tgtPeers = nodeToPeers.get(e.target);
    if (!tgtPeers) {
      tgtPeers = new Set<string>();
      nodeToPeers.set(e.target, tgtPeers);
    }
    tgtPeers.add(e.source);
  }
  return { nodeToEdges, nodeToPeers };
}

type FocusKind = "selection" | "hover" | null;

// Endpoint card is 220px + 24px gap = 244px per column. Thresholds sit ~24px
// above the band width each column count unlocks (952/708/464), leaving margin
// for the boundary frame's border + padding. Used only by the L3-of-external
// grid; every other level ignores it.
function l3ColumnsForWidth(width: number): number {
  if (width >= 976) return 4;
  if (width >= 732) return 3;
  return 2;
}

export function GraphCanvas() {
  const { state, dispatch, extras } = useArchViz();
  const [l3Columns, setL3Columns] = useState(4);
  const computed = useGraphElements(state, { l3ExternalColumns: l3Columns });

  const selectedNodeId = state.selectedNodeId;
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  // Edge hover surfaces a cursor-following tooltip with the full untruncated
  // label text. Set on edge enter, updated on edge move, cleared on leave.
  const [edgeTooltip, setEdgeTooltip] = useState<{
    x: number;
    y: number;
    label: string;
  } | null>(null);

  // Selection wins over hover — once a node is clicked, hovering elsewhere is
  // ignored until the user clears the selection (Esc or pane click).
  const focusNodeId: string | null = selectedNodeId ?? hoveredNodeId;
  const focusKind: FocusKind =
    selectedNodeId !== null ? "selection" : hoveredNodeId !== null ? "hover" : null;

  const { nodeToEdges, nodeToPeers } = useMemo(
    () => buildAdjacency(computed.edges),
    [computed.edges],
  );
  const relatedEdgeIds = useMemo<ReadonlySet<string>>(
    () => (focusNodeId ? (nodeToEdges.get(focusNodeId) ?? new Set()) : new Set()),
    [focusNodeId, nodeToEdges],
  );
  const relatedNodeIds = useMemo<ReadonlySet<string>>(
    () => (focusNodeId ? (nodeToPeers.get(focusNodeId) ?? new Set()) : new Set()),
    [focusNodeId, nodeToPeers],
  );

  // Search filter — only takes effect when the user is NOT actively focusing
  // a node (selection or hover wins). Matched nodes brighten via the existing
  // archviz-focus-* classes; non-matches dim.
  const searchQuery = state.searchQuery.trim().toLowerCase();
  const searchActive = searchQuery.length > 0 && focusKind === null;
  const matchedNodeIds = useMemo<ReadonlySet<string> | null>(() => {
    if (!searchActive) return null;
    const set = new Set<string>();
    for (const n of computed.nodes) {
      if (NON_FOCUSABLE_TYPES.has(n.type ?? "")) continue;
      const label = nodeLabelFor(n);
      if (label.toLowerCase().includes(searchQuery)) set.add(n.id);
    }
    return set;
  }, [searchActive, searchQuery, computed.nodes]);

  const decoratedNodes = useMemo<Node[]>(() => {
    return computed.nodes.map((n) => {
      const elementId = stripPrefix(n.id);
      const count = extras.commentCounts[elementId] ?? 0;
      const carry = extras.carryOverCounts[elementId] ?? 0;
      const selected = n.id === selectedNodeId;

      let focusClass = "";
      if (focusKind && !NON_FOCUSABLE_TYPES.has(n.type ?? "")) {
        const isSelf = n.id === focusNodeId;
        const isRelated = relatedNodeIds.has(n.id);
        const prefix = focusKind === "selection" ? "archviz-focus" : "archviz-hover";
        focusClass = isSelf ? `${prefix}-self` : isRelated ? `${prefix}-related` : `${prefix}-dim`;
      } else if (matchedNodeIds && !NON_FOCUSABLE_TYPES.has(n.type ?? "")) {
        focusClass = matchedNodeIds.has(n.id) ? "archviz-focus-self" : "archviz-focus-dim";
      }

      return {
        ...n,
        selected,
        className: cn(n.className, focusClass),
        data: { ...n.data, commentCount: count, carryOverCount: carry },
      };
    });
  }, [
    computed.nodes,
    extras.commentCounts,
    extras.carryOverCounts,
    selectedNodeId,
    focusKind,
    focusNodeId,
    relatedNodeIds,
    matchedNodeIds,
  ]);

  const decoratedEdges = useMemo<Edge[]>(() => {
    return computed.edges.map((e) => {
      const hoverLabel = (e.data as { hoverLabel?: string } | undefined)?.hoverLabel;

      let focusClass = "";
      let revealLabel: string | undefined;
      if (focusKind) {
        const isRelated = relatedEdgeIds.has(e.id);
        const prefix = focusKind === "selection" ? "archviz-focus" : "archviz-hover";
        focusClass = isRelated ? `${prefix}-edge-related` : `${prefix}-edge-dim`;
        // Surface related-edge labels for BOTH selection and hover focus —
        // the latter is the "teach layer", showing what would happen if the
        // user committed. The inline label only fires when the graph's
        // labelMode is "hover-only" (default at L1, and at L2/L3 after gap
        // #2); the "always" mode already has e.label set.
        if (isRelated && hoverLabel && !e.label) {
          revealLabel = hoverLabel;
        }
      } else if (matchedNodeIds) {
        const bothMatch = matchedNodeIds.has(e.source) && matchedNodeIds.has(e.target);
        focusClass = bothMatch ? "archviz-focus-edge-related" : "archviz-focus-edge-dim";
      }

      const style: CSSProperties = { ...(e.style ?? {}) };
      const next: Edge = { ...e, style, className: cn(e.className, focusClass) };
      // Direct edge hover no longer surfaces an inline midpoint label —
      // it's been replaced by the cursor-following tooltip rendered below,
      // which can show the full untruncated text. Inline labels still
      // appear via revealLabel when a connected node has focus.
      if (revealLabel) {
        next.label = revealLabel;
        next.labelStyle = { fontSize: 10, fill: "var(--muted-foreground)" };
        next.labelBgStyle = { fill: "var(--card)", fillOpacity: 0.9 };
      }
      return next;
    });
  }, [computed.edges, focusKind, relatedEdgeIds, matchedNodeIds]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(decoratedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(decoratedEdges);
  const { fitView } = useReactFlow();

  // Responsive fitView state — see the resize-observer effect below.
  // userInteractedRef goes true on the first real pan/zoom so subsequent
  // container resizes respect the user's chosen view. interactingRef is the
  // belt-and-braces gate that blocks resize-driven fits while a gesture
  // (pan, zoom, node drag) is mid-flight.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const userInteractedRef = useRef(false);
  const interactingRef = useRef(false);
  const resizeTimerRef = useRef<number | undefined>(undefined);
  const firstResizeRef = useRef(true);

  useEffect(() => {
    setNodes(decoratedNodes);
    setEdges(decoratedEdges);
  }, [decoratedNodes, decoratedEdges, setNodes, setEdges]);

  // Re-fit only when the user navigates to a different view (drill in/out,
  // jump levels, load a new architecture, or flip the L1 layout mode).
  // Selection and hover don't change topology, so the camera doesn't jump
  // mid-interaction. ReactFlow's `fitView` prop handles the initial mount
  // fit, so this effect skips its first run via the ref comparator. Reset
  // userInteractedRef on every topology change — a fresh level gets fresh
  // auto-fit-on-resize license.
  const topologyKey = `${state.drillLevel}|${state.selectedContainerId ?? ""}|${state.architecture?.meta?.name ?? ""}|${state.layoutMode}`;
  const lastTopologyRef = useRef(topologyKey);
  useEffect(() => {
    if (lastTopologyRef.current === topologyKey) return;
    lastTopologyRef.current = topologyKey;
    userInteractedRef.current = false;
    const id = requestAnimationFrame(() => fitView({ padding: 0.2, duration: 400 }));
    return () => cancelAnimationFrame(id);
  }, [topologyKey, fitView]);

  // Selection clear (Esc / pane click) is the other "I'm done staking out a
  // view" signal — license auto-fit-on-resize again.
  useEffect(() => {
    if (selectedNodeId === null) userInteractedRef.current = false;
  }, [selectedNodeId]);

  // ResizeObserver on the canvas wrapper — re-fits on window resize, sidebar
  // widening, fullscreen, and DetailPanel/RisksSheet open/close (those panels
  // are flex siblings, so their mount/unmount shrinks/grows the canvas's
  // parent and triggers RO here). Debounced 60ms; gated against user
  // interaction so we don't fight a manual view.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? el.clientWidth;
      const cols = l3ColumnsForWidth(width);
      setL3Columns((prev) => (prev === cols ? prev : cols));
      if (firstResizeRef.current) {
        firstResizeRef.current = false;
        return;
      }
      if (resizeTimerRef.current !== undefined) {
        window.clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = window.setTimeout(() => {
        if (userInteractedRef.current || interactingRef.current) return;
        fitView({ padding: 0.1, duration: 200 });
      }, 60);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (resizeTimerRef.current !== undefined) {
        window.clearTimeout(resizeTimerRef.current);
      }
    };
  }, [fitView]);

  // Esc clears selection. Pane click already does the same via onPaneClick.
  useEffect(() => {
    if (selectedNodeId === null) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") dispatch({ type: "SELECT_NODE", nodeId: null });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedNodeId, dispatch]);

  const onNodeClick: NodeMouseHandler = (_, node) => {
    // systemFrame is purely decorative chrome and stays unselectable.
    // systemBoundary (v0.2's central system box) was previously skipped here
    // — gap #4/#5 made it selectable so its description + drill CTA can live
    // in the side panel like every other entity. DetailPanel's resolveSelection
    // synthesizes a system entity from arch.meta for the v0.2 case.
    if (node.type === "systemFrame") return;
    dispatch({ type: "SELECT_NODE", nodeId: node.id });
  };

  const onNodeDoubleClick: NodeMouseHandler = (_, node) => {
    if (node.type === "systemBoundary" || node.type === "system") {
      dispatch({ type: "JUMP_TO_LEVEL", level: 2 });
      return;
    }
    if (node.type === "container") {
      const containerId = node.id.replace(/^container:/, "");
      dispatch({ type: "DRILL_INTO_CONTAINER", containerId });
      return;
    }
    if (node.type === "externalSystem") {
      const externalId = node.id.replace(/^external:/, "");
      // Only drill when the external has API surface detail to show.
      // Guardrail 2 from the v0.4 design: trivial externals (fonts, CDNs,
      // simple webhooks) don't earn an L3 view.
      const ext = state.architecture?.externalSystems.find((e) => e.id === externalId);
      const hasSurface =
        (ext?.permissions && ext.permissions.length > 0) ||
        (ext?.endpoints && ext.endpoints.length > 0);
      if (!hasSurface) return;
      dispatch({ type: "DRILL_INTO_EXTERNAL", externalId });
    }
  };

  const onNodeMouseEnter: NodeMouseHandler = (_, node) => {
    if (NON_FOCUSABLE_TYPES.has(node.type ?? "")) {
      setHoveredNodeId(null);
      return;
    }
    setHoveredNodeId(node.id);
  };
  const onNodeMouseLeave: NodeMouseHandler = () => {
    setHoveredNodeId(null);
  };

  const onPaneClick = () => {
    if (selectedNodeId !== null) dispatch({ type: "SELECT_NODE", nodeId: null });
  };

  const edgeTooltipLabel = (edge: Edge): string | null => {
    const fromData = (edge.data as { hoverLabel?: string } | undefined)?.hoverLabel;
    if (fromData) return fromData;
    if (typeof edge.label === "string") return edge.label;
    return null;
  };

  const onEdgeMouseEnter: EdgeMouseHandler = (event, edge) => {
    const label = edgeTooltipLabel(edge);
    if (!label) return;
    setEdgeTooltip({ x: event.clientX, y: event.clientY, label });
  };
  const onEdgeMouseMove: EdgeMouseHandler = (event, edge) => {
    const label = edgeTooltipLabel(edge);
    if (!label) return;
    setEdgeTooltip({ x: event.clientX, y: event.clientY, label });
  };
  const onEdgeMouseLeave: EdgeMouseHandler = () => {
    setEdgeTooltip(null);
  };

  return (
    <>
      <ErdEdgeMarkers />
      <div ref={wrapperRef} className="h-full w-full bg-surface-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodeMouseEnter={onNodeMouseEnter}
          onNodeMouseLeave={onNodeMouseLeave}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseMove={onEdgeMouseMove}
          onEdgeMouseLeave={onEdgeMouseLeave}
          onPaneClick={onPaneClick}
          onMoveStart={() => {
            interactingRef.current = true;
          }}
          onMoveEnd={() => {
            interactingRef.current = false;
          }}
          onMove={(event) => {
            // Programmatic moves (our own fitView calls) pass event=null;
            // only flag genuine user pan/zoom so we don't self-trigger
            // the userInteracted gate.
            if (event) userInteractedRef.current = true;
          }}
          onNodeDragStart={() => {
            interactingRef.current = true;
          }}
          onNodeDragStop={() => {
            interactingRef.current = false;
          }}
          nodeTypes={NODE_TYPES}
          defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
          fitView
          proOptions={{ hideAttribution: true }}
          minZoom={0.1}
          maxZoom={2}
        >
          <Background gap={24} color="var(--canvas-dot)" />
          <MiniMap pannable zoomable />
          <Controls />
          <DataViewSwitch />
          <CanvasControls />
        </ReactFlow>
      </div>
      {edgeTooltip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 max-w-[240px] rounded-md bg-tooltip-bg text-tooltip-fg shadow-md px-2.5 py-1.5 text-caption leading-snug"
          style={{ left: edgeTooltip.x + 12, top: edgeTooltip.y + 12 }}
        >
          {edgeTooltip.label}
        </div>
      )}
    </>
  );
}
